'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import useEmblaCarousel from 'embla-carousel-react';
import type { ProductListItem } from '@cobrastyle/shared-types';
import type { ProductExtras } from '@/lib/product-extras';
import { ProductCard } from '@/components/product/product-card';

/** Horizontal, arrow-navigated carousel of product cards. */
export function ProductCarousel({
  products,
  extras,
}: {
  products: ProductListItem[];
  /** Keyed by SKU. A product absent from the map renders a card without brand. */
  extras?: Map<string, ProductExtras>;
}) {
  const t = useTranslations('common');
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', containScroll: 'trimSnaps' });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-3">
          {products.map((product) => (
            <div
              key={product.sku}
              className="min-w-0 flex-[0_0_40%] sm:flex-[0_0_26%] md:flex-[0_0_22%] lg:flex-[0_0_18%] xl:flex-[0_0_16%]"
            >
              <ProductCard product={product} extras={extras?.get(product.sku)} />
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        aria-label={t('previousProducts')}
        onClick={() => emblaApi?.scrollPrev()}
        disabled={!canPrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full border border-border bg-card p-2 shadow-md transition-opacity disabled:pointer-events-none disabled:opacity-0"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        type="button"
        aria-label={t('nextProducts')}
        onClick={() => emblaApi?.scrollNext()}
        disabled={!canNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 rounded-full border border-border bg-card p-2 shadow-md transition-opacity disabled:pointer-events-none disabled:opacity-0"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}
